import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { useClients, useContracts, useData, useConvenios } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { ClientEstadisticas } from '../../components/ClientEstadisticas';
import SearchableListbox from '../../components/SearchableListbox';
import ContratoFianzasPanel from '../../components/convenio/ContratoFianzasPanel';
import { ContratoView } from '../../components/ContratoView';
import { FianzaView } from '../../components/FianzaView';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ESTADO_FIANZA } from '../../utils/fianzaEventos';
import { usePortalView } from '../../contexts/PortalViewContext';
import { getClientExpiryStatus } from '../../utils/documentExpiry';
import { getEmisorNombre } from '../../utils/calculations';
import {
  aggregateProveedores,
  computeConvenioTotals,
  computeEmittedFianzas,
  computePendingFianzas,
  dueLabel,
  getFianzaStatus,
} from '../../utils/convenioCompliance';

const formatMXN = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const formatDate = (ts) => {
  if (!ts) return '-';
  const ms = ts?.toMillis?.() || ts;
  return new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const escapeCSVCell = (val) => {
  const s = val === null || val === undefined ? '' : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename, headers, rows) => {
  const csv = [headers, ...rows]
    .map((r) => r.map(escapeCSVCell).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const CLIENT_REPORTS = [
  { id: 'cliente-estadisticas', title: 'Estadísticas', description: 'Distribución por tipo y afianzadora de tus fianzas, con historial por año.' },
  { id: 'cliente-desglose', title: 'Desglose', description: 'Desglose por contrato con descarga en CSV.' },
  { id: 'cliente-vencimientos', title: 'Vencimientos', description: 'Fianzas vigentes por fecha de vencimiento, con descarga en CSV.' },
  { id: 'cliente-por-emitir', title: 'Por emitir', description: 'Requisitos sin fianza emitida: atrasados, por emitir y futuros, con descarga en CSV.' },
  { id: 'expedientes', title: 'Expedientes', description: 'Vigencia y estado de tus documentos.' },
];

const CONVENIO_REPORTS = [
  { id: 'convenios-estadisticas', title: 'Estadísticas', description: 'Distribución por proveedor, tipo y afianzadora del convenio, con historial por año.' },
  { id: 'convenios-resumen', title: 'Desglose', description: 'Desglose por convenio con descarga en CSV.' },
  { id: 'convenios-vencimientos', title: 'Vencimientos', description: 'Fianzas vigentes de tus convenios por fecha de vencimiento, con descarga en CSV.' },
  { id: 'convenios-por-emitir', title: 'Por emitir', description: 'Requisitos sin fianza emitida: atrasados, por emitir y futuros, con descarga en CSV.' },
  { id: 'convenios-proveedores', title: 'Proveedores', description: 'Listado completo de proveedores de tus convenios con búsqueda y descarga en CSV.' },
];

const VENCIMIENTO_HORIZONS = [
  { value: '30', label: 'Vence en ≤ 30 días' },
  { value: '60', label: 'Vence en ≤ 60 días' },
  { value: '90', label: 'Vence en ≤ 90 días' },
  { value: 'all', label: 'Todas las vigentes' },
];

const ReportCard = ({ report, selected, onSelect }) => (
  <button
    onClick={() => onSelect(report.id)}
    className={`w-full text-left bg-white border rounded-xl p-4 shadow-sm transition-all ${
      selected ? 'border-azul-surelia ring-2 ring-azul-surelia/30' : 'border-azul-surelia/15 hover:border-azul-surelia/40'
    }`}
  >
    <Heading level={3} className="text-lg text-azul-surelia mb-1">
      {report.title}
    </Heading>
    <Text className="text-sm text-azul-surelia/70">{report.description}</Text>
  </button>
);

const ClientPortalReportes = () => {
  const navigate = useNavigate();
  const { clientsList: allClients, clientsById } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { linksById } = useData();
  const { conveniosList, conveniosById } = useConvenios();
  const { userData, hasConvenioAccess: hasConvenioAccessRaw } = useUser();
  const { activeClienteId, activeConvenioId } = usePortalView();

  // Scoped to the active portal context: a fiado context shows only the client
  // report set for that empresa; a convenio context shows only the convenio
  // set for that convenio. Without a context (legacy), fall back to everything.
  const clientsList = useMemo(
    () => (activeClienteId
      ? allClients.filter((c) => c.id === activeClienteId)
      : activeConvenioId
      ? []
      : allClients),
    [allClients, activeClienteId, activeConvenioId]
  );
  const hasClientAccess = activeConvenioId ? false : (userData?.clientIds?.length || 0) > 0;
  const hasConvenioAccess = activeClienteId ? false : hasConvenioAccessRaw;

  const accessibleConvenios = useMemo(() => {
    if (!hasConvenioAccess || !userData?.convenioAccess) return [];
    const ids = new Set(Object.keys(userData.convenioAccess));
    const base = conveniosList.filter((c) => ids.has(c.id));
    return activeConvenioId ? base.filter((c) => c.id === activeConvenioId) : base;
  }, [conveniosList, userData?.convenioAccess, hasConvenioAccess, activeConvenioId]);

  const REPORTS = useMemo(() => {
    const reports = [];
    if (hasClientAccess) reports.push(...CLIENT_REPORTS);
    if (hasConvenioAccess) reports.push(...CONVENIO_REPORTS);
    return reports;
  }, [hasClientAccess, hasConvenioAccess]);

  const [selectedReportId, setSelectedReportId] = useState(
    hasClientAccess ? CLIENT_REPORTS[0].id : (hasConvenioAccess ? CONVENIO_REPORTS[0].id : CLIENT_REPORTS[0].id)
  );

  // If access flags arrive after first render, ensure the selected report is one the user can see
  useEffect(() => {
    if (REPORTS.length === 0) return;
    if (!REPORTS.some((r) => r.id === selectedReportId)) {
      setSelectedReportId(REPORTS[0].id);
    }
  }, [REPORTS, selectedReportId]);

  // Fiado-context data: the active empresa's contratos (no convenio contracts
  // mixed in), their emitted fianzas and their pending requisitos — the same
  // rollups every other fiado surface uses.
  const clienteContracts = useMemo(() => {
    const ids = new Set(clientsList.map((c) => c.id));
    return contractsList.filter((c) => c.clientId && ids.has(c.clientId));
  }, [contractsList, clientsList]);

  const clienteEmitted = useMemo(
    () => computeEmittedFianzas(clienteContracts),
    [clienteContracts]
  );

  // Atrasadas first (oldest due date first), then por emitir by due date,
  // futuras at the end — same order as the convenio report.
  const clientePendientes = useMemo(() => {
    return computePendingFianzas(clienteContracts).sort((a, b) => {
      if (a.isFutura !== b.isFutura) return a.isFutura ? 1 : -1;
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if (a.dueDate == null && b.dueDate == null) return 0;
      if (a.dueDate == null) return 1;
      if (b.dueDate == null) return -1;
      return a.dueDate - b.dueDate;
    });
  }, [clienteContracts]);

  const expiryByClient = useMemo(() => {
    const map = {};
    clientsList.forEach((c) => {
      map[c.nombreEmpresa || c.id] = getClientExpiryStatus(c.links || [], linksById);
    });
    return map;
  }, [clientsList, linksById]);

  // Per-convenio rollup
  const convenioBreakdown = useMemo(() => {
    if (!hasConvenioAccess) return [];
    return accessibleConvenios.map((conv) => {
      const access = userData?.convenioAccess?.[conv.id];
      const convContracts = (contractsList || []).filter((c) => c.convenioId === conv.id);
      const filtered = access?.fullAccess
        ? convContracts
        : convContracts.filter((c) => c.proyectoId && (access?.proyectoIds || []).includes(c.proyectoId));
      const beneficiariosById = {};
      (conv.beneficiarios || []).forEach((b) => { beneficiariosById[b.id] = b; });
      const proyectosById = {};
      (conv.proyectos || []).forEach((p) => { proyectosById[p.id] = p; });
      const agg = aggregateProveedores(filtered, beneficiariosById, proyectosById);
      const totals = computeConvenioTotals(agg);
      return { convenio: conv, totals, agg, filtered, beneficiariosById, proyectosById };
    });
  }, [accessibleConvenios, contractsList, userData?.convenioAccess, hasConvenioAccess]);

  // Every emitted fianza / pending requisito across the user's convenios,
  // tagged with its convenio — feeds the Vencimientos and Por emitir reports.
  const allEmittedAcrossConvenios = useMemo(() => {
    const all = [];
    convenioBreakdown.forEach(({ convenio, filtered, beneficiariosById, proyectosById }) => {
      computeEmittedFianzas(filtered, beneficiariosById, proyectosById).forEach((f) =>
        all.push({ ...f, convenioNombre: convenio.nombre, convenioId: convenio.id })
      );
    });
    return all;
  }, [convenioBreakdown]);

  const allPendientesAcrossConvenios = useMemo(() => {
    const all = [];
    convenioBreakdown.forEach(({ convenio, filtered, beneficiariosById, proyectosById }) => {
      computePendingFianzas(filtered, beneficiariosById, proyectosById).forEach((fr) =>
        all.push({ ...fr, convenioNombre: convenio.nombre, convenioId: convenio.id })
      );
    });
    // Atrasadas first (oldest due date first), then por emitir by due date,
    // futuras at the end.
    return all.sort((a, b) => {
      if (a.isFutura !== b.isFutura) return a.isFutura ? 1 : -1;
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if (a.dueDate == null && b.dueDate == null) return 0;
      if (a.dueDate == null) return 1;
      if (b.dueDate == null) return -1;
      return a.dueDate - b.dueDate;
    });
  }, [convenioBreakdown]);

  const allProveedoresAcrossConvenios = useMemo(() => {
    const all = [];
    convenioBreakdown.forEach(({ convenio, agg }) => {
      agg.forEach((p) => all.push({ ...p, convenioNombre: convenio.nombre, convenioId: convenio.id }));
    });
    return all.sort((a, b) => {
      if (a.montos.overdue !== b.montos.overdue) return b.montos.overdue - a.montos.overdue;
      if (a.montos.pendiente !== b.montos.pendiente) return b.montos.pendiente - a.montos.pendiente;
      return b.montos.total - a.montos.total;
    });
  }, [convenioBreakdown]);

  const [proveedorSearch, setProveedorSearch] = useState('');
  const [expandedProveedorKeys, setExpandedProveedorKeys] = useState(() => new Set());
  const [estadisticasClienteId, setEstadisticasClienteId] = useState(null);
  const [estadisticasConvenioId, setEstadisticasConvenioId] = useState(null);
  const [estadisticasProyectoId, setEstadisticasProyectoId] = useState('all');
  const [vencimientosHorizon, setVencimientosHorizon] = useState('90');

  const toggleProveedorExpanded = (key) => {
    setExpandedProveedorKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // In-page viewers: a fianza row opens FianzaView, a por-emitir requisito
  // opens its ContratoView — no detour through the convenio page.
  const [viewContrato, setViewContrato] = useState(null); // { contract, convenio }
  const [viewFianza, setViewFianza] = useState(null); // { fianza, contract, convenio }

  const beneficiariosByIdFor = (convenio) => {
    const map = {};
    (convenio?.beneficiarios || []).forEach((b) => { map[b.id] = b; });
    return map;
  };

  const openContratoView = (contractId, convenioId) => {
    const contract = contractsList.find((c) => c.id === contractId);
    if (!contract) return;
    setViewContrato({ contract, convenio: conveniosById[convenioId] || null });
  };

  const openFianzaView = (fianza, contractId, convenioId) => {
    const contract = contractsList.find((c) => c.id === contractId);
    if (!contract) return;
    setViewFianza({
      fianza: { ...fianza, contratoId: contract.id },
      contract,
      convenio: conveniosById[convenioId] || null,
    });
  };
  const filteredProveedores = useMemo(() => {
    const q = proveedorSearch.trim().toLowerCase();
    if (!q) return allProveedoresAcrossConvenios;
    return allProveedoresAcrossConvenios.filter((p) =>
      (p.label || '').toLowerCase().includes(q) ||
      (p.rfc || '').toLowerCase().includes(q) ||
      (p.convenioNombre || '').toLowerCase().includes(q)
    );
  }, [allProveedoresAcrossConvenios, proveedorSearch]);

  if (contractsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  const renderReport = () => {
    switch (selectedReportId) {
      case 'cliente-estadisticas': {
        const selectedClienteId =
          estadisticasClienteId && clientsList.some((c) => c.id === estadisticasClienteId)
            ? estadisticasClienteId
            : clientsList[0]?.id || null;
        if (!selectedClienteId) {
          return (
            <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
              <Text>No hay empresas asignadas.</Text>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            {clientsList.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Empresa:</label>
                <SearchableListbox
                  value={selectedClienteId}
                  onChange={setEstadisticasClienteId}
                  options={clientsList.map((c) => ({ value: c.id, label: c.nombreEmpresa || c.id }))}
                  className="min-w-[240px] max-w-[320px]"
                  placeholder="Selecciona empresa"
                  searchPlaceholder="Buscar empresa..."
                  popupClassName="w-[300px]"
                />
              </div>
            )}
            <ClientEstadisticas clientId={selectedClienteId} mode="client" />
          </div>
        );
      }

      case 'cliente-desglose': {
        const now = Date.now();
        const rows = clienteContracts.map((c) => {
          let vigentes = 0;
          let vencidas = 0;
          let porIniciar = 0;
          let terminadas = 0;
          let montoAfianzado = 0;
          (c.fianzas || []).forEach((f) => {
            const status = getFianzaStatus(f, now);
            if (status.estado === ESTADO_FIANZA.ACTIVA) {
              montoAfianzado += Number(f.montoAfianzado) || 0;
              if (status.code === 'vigente') vigentes++;
              else if (status.code === 'vencida') vencidas++;
              else porIniciar++;
            } else {
              terminadas++;
            }
          });
          const porEmitir = computePendingFianzas([c]).filter((fr) => !fr.isFutura).length;
          return {
            contrato: c,
            emitidas: (c.fianzas || []).length,
            vigentes, vencidas, porIniciar, terminadas, montoAfianzado, porEmitir,
          };
        }).sort((a, b) => (Number(b.contrato.montoContrato?.value) || 0) - (Number(a.contrato.montoContrato?.value) || 0));
        const exportRows = rows.map((r) => [
          r.contrato.numeroContrato?.value || '',
          r.contrato.tituloContrato?.value || '',
          r.contrato.beneficiario?.value || r.contrato.contratante?.value || '',
          r.emitidas,
          r.vigentes,
          r.vencidas,
          r.terminadas,
          r.porEmitir,
          Number(r.contrato.montoContrato?.value) || 0,
          r.montoAfianzado,
        ]);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Text className="text-sm text-gray-600">
                {rows.length} contrato{rows.length === 1 ? '' : 's'} con su estado de fianzas.
              </Text>
              <button
                onClick={() => downloadCSV(
                  'desglose-contratos.csv',
                  ['Contrato', 'Título', 'Beneficiario', 'Emitidas', 'Vigentes', 'Vencidas', 'Terminadas', 'Por emitir', 'Monto contrato', 'Monto afianzado'],
                  exportRows
                )}
                disabled={rows.length === 0}
                className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
              >
                Descargar CSV
              </button>
            </div>
            {rows.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>No hay contratos registrados.</Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3">No. de Contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Beneficiario</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Emitidas</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Vigentes</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Vencidas</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Por emitir</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto afianzado</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, idx) => (
                        <TableRow
                          key={r.contrato.id}
                          className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                          onClick={() => openContratoView(r.contrato.id)}
                        >
                          <TableCell className="py-3">
                            <Text className="text-sm font-semibold text-zinc-900 truncate max-w-xs" title={r.contrato.numeroContrato?.value || r.contrato.tituloContrato?.value || '-'}>
                              {r.contrato.numeroContrato?.value || r.contrato.tituloContrato?.value || 'Sin número'}
                            </Text>
                            {r.contrato.numeroContrato?.value && r.contrato.tituloContrato?.value && (
                              <Text className="text-xs text-zinc-500 truncate max-w-xs" title={r.contrato.tituloContrato.value}>
                                {r.contrato.tituloContrato.value}
                              </Text>
                            )}
                          </TableCell>
                          <TableCell className="py-3 text-sm">
                            {r.contrato.beneficiario?.value || r.contrato.contratante?.value || '-'}
                          </TableCell>
                          <TableCell className="py-3"><Text className="text-sm font-semibold">{r.emitidas}</Text></TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${r.vigentes > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                              {r.vigentes}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${r.vencidas > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`}>
                              {r.vencidas}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${r.porEmitir > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                              {r.porEmitir}
                            </span>
                          </TableCell>
                          <TableCell className="py-3"><Text className="text-sm font-semibold text-azul-surelia">{formatMXN(Number(r.contrato.montoContrato?.value) || 0)}</Text></TableCell>
                          <TableCell className="py-3"><Text className="text-sm font-semibold text-azul-surelia">{formatMXN(r.montoAfianzado)}</Text></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'cliente-vencimientos': {
        const now = Date.now();
        const vigentes = clienteEmitted
          .map((f) => ({ f, status: getFianzaStatus(f, now) }))
          .filter(({ status }) => status.code === 'vigente')
          .map(({ f }) => ({ ...f, _fin: f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0 }))
          .filter((f) => f._fin > 0)
          .sort((a, b) => a._fin - b._fin);
        const horizonRows =
          vencimientosHorizon === 'all'
            ? vigentes
            : vigentes.filter((f) => f._fin <= now + Number(vencimientosHorizon) * 86400000);
        const exportRows = horizonRows.map((f) => [
          f.numeroFianza || '',
          f.tipoFianza || f.tipo || '',
          f.contractNumero || f.contractTitle || '',
          getEmisorNombre(f) || '',
          Number(f.montoAfianzado) || 0,
          formatDate(f._fin),
          Math.ceil((f._fin - now) / 86400000),
        ]);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <select
                  value={vencimientosHorizon}
                  onChange={(e) => setVencimientosHorizon(e.target.value)}
                  className="text-sm border border-azul-surelia/30 rounded-md px-2 py-1.5 text-azul-surelia"
                >
                  {VENCIMIENTO_HORIZONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <Text className="text-xs text-zinc-500">
                  {horizonRows.length} fianza{horizonRows.length === 1 ? '' : 's'}
                </Text>
              </div>
              <button
                onClick={() => downloadCSV(
                  'vencimientos.csv',
                  ['No. Fianza', 'Tipo', 'Contrato', 'Afianzadora', 'Monto', 'Fin de vigencia', 'Días restantes'],
                  exportRows
                )}
                disabled={horizonRows.length === 0}
                className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
              >
                Descargar CSV
              </button>
            </div>
            {horizonRows.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>
                  {vencimientosHorizon === 'all'
                    ? 'No hay fianzas vigentes.'
                    : 'Ninguna fianza vigente vence en este horizonte.'}
                </Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3">No. Fianza</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Tipo</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Afianzadora</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Fin de vigencia</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Días</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {horizonRows.map((f, idx) => {
                        const dias = Math.ceil((f._fin - now) / 86400000);
                        return (
                          <TableRow
                            key={`${f.contractId}_${f.id || idx}`}
                            className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => openFianzaView(f, f.contractId)}
                          >
                            <TableCell className="py-3 text-sm font-mono">{f.numeroFianza || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{f.tipoFianza || f.tipo || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">
                              <span className="truncate max-w-[220px] inline-block align-bottom" title={f.contractTitle || ''}>
                                {f.contractNumero || f.contractTitle || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="py-3 text-sm">{getEmisorNombre(f) || '-'}</TableCell>
                            <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                              {formatMXN(f.montoAfianzado || 0)}
                            </TableCell>
                            <TableCell className="py-3 text-xs text-gray-600">{formatDate(f._fin)}</TableCell>
                            <TableCell className="py-3">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                                dias <= 30 ? 'bg-red-100 text-red-800' : dias <= 60 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {dias} día{dias === 1 ? '' : 's'}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'cliente-por-emitir': {
        const exportRows = clientePendientes.map((fr) => [
          fr.isFutura ? 'Futura' : fr.isOverdue ? 'Atrasada' : 'Por emitir',
          fr.tipo || 'Fianza',
          fr.contractNumero || fr.contractTitle || '',
          Number(fr.porcentaje) > 0 ? Number(fr.porcentaje) : '',
          Number(fr.monto) || 0,
          dueLabel(fr),
        ]);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Text className="text-sm text-gray-600">
                {clientePendientes.length} requisito{clientePendientes.length === 1 ? '' : 's'} sin fianza emitida.
              </Text>
              <button
                onClick={() => downloadCSV(
                  'por-emitir.csv',
                  ['Estado', 'Tipo', 'Contrato', '% del contrato', 'Por afianzar', 'Emisión'],
                  exportRows
                )}
                disabled={clientePendientes.length === 0}
                className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
              >
                Descargar CSV
              </button>
            </div>
            {clientePendientes.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>Todas las fianzas requeridas ya fueron emitidas.</Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3">Estado</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Tipo</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">% del contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3 text-right">Por afianzar</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Emisión</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientePendientes.map((fr, idx) => {
                        const tone = fr.isFutura
                          ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                          : fr.isOverdue
                          ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                          : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                        return (
                          <TableRow
                            key={fr._key}
                            className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => openContratoView(fr.contractId)}
                          >
                            <TableCell className="py-3">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>{tone.label}</span>
                            </TableCell>
                            <TableCell className="py-3 text-sm font-medium text-zinc-800">{fr.tipo || 'Fianza'}</TableCell>
                            <TableCell className="py-3 text-sm">
                              <span className="truncate max-w-[220px] inline-block align-bottom" title={fr.contractTitle || ''}>
                                {fr.contractNumero || fr.contractTitle || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="py-3 text-sm tabular-nums">
                              {Number(fr.porcentaje) > 0 ? `${Number(fr.porcentaje)}%` : '—'}
                            </TableCell>
                            <TableCell className="py-3 text-sm text-right tabular-nums font-semibold text-azul-surelia">
                              {formatMXN(fr.monto || 0)}
                            </TableCell>
                            <TableCell className={`py-3 text-xs ${tone.due}`}>{dueLabel(fr)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'expedientes':
        return (
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-x-auto">
            {clientsList.length === 0 ? (
              <Text className="text-zinc-500 py-8 text-center">No hay empresas asignadas.</Text>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                  <TableRow>
                    <TableHeader className="text-white font-semibold py-3">Empresa</TableHeader>
                    <TableHeader className="text-white font-semibold py-3">Docs vencidos</TableHeader>
                    <TableHeader className="text-white font-semibold py-3">Docs por vencer</TableHeader>
                    <TableHeader className="text-white font-semibold py-3">Estado</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(expiryByClient).map(([empresa, info], idx) => (
                    <TableRow key={empresa} className={idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}>
                      <TableCell className="py-3"><Text className="text-sm font-medium">{empresa}</Text></TableCell>
                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                          info.expiredCount > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {info.expiredCount}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                          info.warningCount > 0 ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {info.warningCount}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                          info.worst === 'expired' ? 'bg-red-100 text-red-800'
                          : info.worst === 'warning' ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                        }`}>
                          {info.worst === 'expired' ? 'Vencido' : info.worst === 'warning' ? 'Por vencer' : 'Al día'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </div>
        );

      case 'convenios-resumen': {
        const exportRows = convenioBreakdown.map(({ convenio, totals }) => [
          convenio.nombre,
          totals.totalProveedores,
          totals.totalContratos,
          totals.fianzasVigentes,
          totals.fianzasVencidas,
          totals.fianzasPendientes,
          totals.montoTotal,
          totals.montoPendiente,
          totals.montoVencido,
        ]);
        return (
          <div className="space-y-6">
            {convenioBreakdown.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>No hay convenios disponibles.</Text>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <Heading level={3} className="text-azul-surelia">Desglose por convenio</Heading>
                  <button
                    onClick={() => downloadCSV(
                      'convenios-resumen.csv',
                      ['Convenio', 'Proveedores', 'Contratos', 'Vigentes', 'Vencidas', 'Pendientes', 'Monto afianzado', 'Monto pendiente', 'Monto vencido'],
                      exportRows
                    )}
                    className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5"
                  >
                    Descargar CSV
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                        <TableRow>
                          <TableHeader className="text-white font-semibold py-3">Convenio</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Proveedores</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Contratos</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Vigentes</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Vencidas</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Pendientes</TableHeader>
                          <TableHeader className="text-white font-semibold py-3">Monto afianzado</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {convenioBreakdown.map(({ convenio, totals }, idx) => (
                          <TableRow
                            key={convenio.id}
                            className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => navigate(`/app/portal/convenios/${convenio.id}`)}
                          >
                            <TableCell className="py-3"><Text className="text-sm font-semibold text-azul-surelia">{convenio.nombre}</Text></TableCell>
                            <TableCell className="py-3"><Text className="text-sm">{totals.totalProveedores}</Text></TableCell>
                            <TableCell className="py-3"><Text className="text-sm">{totals.totalContratos}</Text></TableCell>
                            <TableCell className="py-3">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${totals.fianzasVigentes > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                {totals.fianzasVigentes}
                              </span>
                            </TableCell>
                            <TableCell className="py-3">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${totals.fianzasVencidas > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`}>
                                {totals.fianzasVencidas}
                              </span>
                            </TableCell>
                            <TableCell className="py-3">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${totals.fianzasPendientes > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                                {totals.fianzasPendientes}
                              </span>
                            </TableCell>
                            <TableCell className="py-3"><Text className="text-sm font-semibold text-azul-surelia">{formatMXN(totals.montoTotal)}</Text></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'convenios-estadisticas': {
        const selectedConvenioId =
          estadisticasConvenioId && accessibleConvenios.some((c) => c.id === estadisticasConvenioId)
            ? estadisticasConvenioId
            : accessibleConvenios[0]?.id || null;
        if (!selectedConvenioId) {
          return (
            <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
              <Text>No hay convenios disponibles.</Text>
            </div>
          );
        }
        // Whole convenio or one proyecto. Proyecto-scoped users only get their
        // proyectos as options (their contracts are rules-filtered anyway).
        const selectedConvenio = accessibleConvenios.find((c) => c.id === selectedConvenioId);
        const access = userData?.convenioAccess?.[selectedConvenioId];
        const proyectoOptions = (selectedConvenio?.proyectos || []).filter(
          (p) => access?.fullAccess || (access?.proyectoIds || []).includes(p.id)
        );
        const selectedProyectoId =
          estadisticasProyectoId !== 'all' && proyectoOptions.some((p) => p.id === estadisticasProyectoId)
            ? estadisticasProyectoId
            : null;
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              {accessibleConvenios.length > 1 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Convenio:</label>
                  <SearchableListbox
                    value={selectedConvenioId}
                    onChange={(id) => { setEstadisticasConvenioId(id); setEstadisticasProyectoId('all'); }}
                    options={accessibleConvenios.map((c) => ({ value: c.id, label: c.nombre }))}
                    className="min-w-[240px] max-w-[320px]"
                    placeholder="Selecciona convenio"
                    searchPlaceholder="Buscar convenio..."
                    popupClassName="w-[300px]"
                  />
                </div>
              )}
              {proyectoOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Proyecto:</label>
                  <SearchableListbox
                    value={selectedProyectoId || 'all'}
                    onChange={setEstadisticasProyectoId}
                    options={[
                      { value: 'all', label: 'Todo el convenio' },
                      ...proyectoOptions.map((p) => ({ value: p.id, label: p.nombre })),
                    ]}
                    className="min-w-[220px] max-w-[300px]"
                    placeholder="Selecciona proyecto"
                    searchPlaceholder="Buscar proyecto..."
                    popupClassName="w-[280px]"
                  />
                </div>
              )}
            </div>
            <ClientEstadisticas convenioId={selectedConvenioId} proyectoId={selectedProyectoId} mode="client" />
          </div>
        );
      }

      case 'convenios-vencimientos': {
        const now = Date.now();
        const vigentes = allEmittedAcrossConvenios
          .map((f) => ({ f, status: getFianzaStatus(f, now) }))
          .filter(({ status }) => status.code === 'vigente')
          .map(({ f }) => ({ ...f, _fin: f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0 }))
          .filter((f) => f._fin > 0)
          .sort((a, b) => a._fin - b._fin);
        const horizonRows =
          vencimientosHorizon === 'all'
            ? vigentes
            : vigentes.filter((f) => f._fin <= now + Number(vencimientosHorizon) * 86400000);
        const exportRows = horizonRows.map((f) => [
          f.numeroFianza || '',
          f.tipoFianza || f.tipo || '',
          f.convenioNombre,
          f.contractNumero || '',
          f.contractProveedor || '',
          f.proyectoNombre || '',
          Number(f.montoAfianzado) || 0,
          formatDate(f._fin),
          Math.ceil((f._fin - now) / 86400000),
        ]);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <select
                  value={vencimientosHorizon}
                  onChange={(e) => setVencimientosHorizon(e.target.value)}
                  className="text-sm border border-azul-surelia/30 rounded-md px-2 py-1.5 text-azul-surelia"
                >
                  {VENCIMIENTO_HORIZONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <Text className="text-xs text-zinc-500">
                  {horizonRows.length} fianza{horizonRows.length === 1 ? '' : 's'}
                </Text>
              </div>
              <button
                onClick={() => downloadCSV(
                  'convenios-vencimientos.csv',
                  ['No. Fianza', 'Tipo', 'Convenio', 'Contrato', 'Proveedor', 'Proyecto', 'Monto', 'Fin de vigencia', 'Días restantes'],
                  exportRows
                )}
                disabled={horizonRows.length === 0}
                className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
              >
                Descargar CSV
              </button>
            </div>
            {horizonRows.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>
                  {vencimientosHorizon === 'all'
                    ? 'No hay fianzas vigentes en tus convenios.'
                    : 'Ninguna fianza vigente vence en este horizonte.'}
                </Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3">No. Fianza</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Tipo</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Convenio</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Proveedor</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Proyecto</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Fin de vigencia</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Días</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {horizonRows.map((f, idx) => {
                        const dias = Math.ceil((f._fin - now) / 86400000);
                        return (
                          <TableRow
                            key={`${f.contractId}_${f.id}`}
                            className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => openFianzaView(f, f.contractId, f.convenioId)}
                          >
                            <TableCell className="py-3 text-sm font-mono">{f.numeroFianza || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{f.tipoFianza || f.tipo || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{f.convenioNombre}</TableCell>
                            <TableCell className="py-3 text-sm">{f.contractNumero || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{f.contractProveedor || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{f.proyectoNombre || '-'}</TableCell>
                            <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                              {formatMXN(f.montoAfianzado || 0)}
                            </TableCell>
                            <TableCell className="py-3 text-xs text-gray-600">{formatDate(f._fin)}</TableCell>
                            <TableCell className="py-3">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                                dias <= 30 ? 'bg-red-100 text-red-800' : dias <= 60 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {dias} día{dias === 1 ? '' : 's'}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'convenios-por-emitir': {
        const exportRows = allPendientesAcrossConvenios.map((fr) => [
          fr.isFutura ? 'Futura' : fr.isOverdue ? 'Atrasada' : 'Por emitir',
          fr.tipo || 'Fianza',
          fr.convenioNombre,
          fr.contractNumero || '',
          fr.fiadoNombre || '',
          fr.proyectoNombre || '',
          Number(fr.porcentaje) > 0 ? Number(fr.porcentaje) : '',
          Number(fr.monto) || 0,
          dueLabel(fr),
        ]);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Text className="text-sm text-gray-600">
                {allPendientesAcrossConvenios.length} requisito{allPendientesAcrossConvenios.length === 1 ? '' : 's'} sin fianza emitida en tus convenios.
              </Text>
              <button
                onClick={() => downloadCSV(
                  'convenios-por-emitir.csv',
                  ['Estado', 'Tipo', 'Convenio', 'Contrato', 'Proveedor', 'Proyecto', '% del contrato', 'Por afianzar', 'Emisión'],
                  exportRows
                )}
                disabled={allPendientesAcrossConvenios.length === 0}
                className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
              >
                Descargar CSV
              </button>
            </div>
            {allPendientesAcrossConvenios.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>Todas las fianzas requeridas de tus convenios ya fueron emitidas.</Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3">Estado</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Tipo</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Convenio</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Proveedor</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Proyecto</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">% del contrato</TableHeader>
                        <TableHeader className="text-white font-semibold py-3 text-right">Por afianzar</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Emisión</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allPendientesAcrossConvenios.map((fr, idx) => {
                        const tone = fr.isFutura
                          ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                          : fr.isOverdue
                          ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                          : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                        return (
                          <TableRow
                            key={`${fr.convenioId}_${fr._key}`}
                            className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => openContratoView(fr.contractId, fr.convenioId)}
                          >
                            <TableCell className="py-3">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>{tone.label}</span>
                            </TableCell>
                            <TableCell className="py-3 text-sm font-medium text-zinc-800">{fr.tipo || 'Fianza'}</TableCell>
                            <TableCell className="py-3 text-sm">{fr.convenioNombre}</TableCell>
                            <TableCell className="py-3 text-sm">{fr.contractNumero || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{fr.fiadoNombre || '-'}</TableCell>
                            <TableCell className="py-3 text-sm">{fr.proyectoNombre || '-'}</TableCell>
                            <TableCell className="py-3 text-sm tabular-nums">
                              {Number(fr.porcentaje) > 0 ? `${Number(fr.porcentaje)}%` : '—'}
                            </TableCell>
                            <TableCell className="py-3 text-sm text-right tabular-nums font-semibold text-azul-surelia">
                              {formatMXN(fr.monto || 0)}
                            </TableCell>
                            <TableCell className={`py-3 text-xs ${tone.due}`}>{dueLabel(fr)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'convenios-proveedores': {
        const terminadasOf = (p) =>
          (p.emitidas || []).filter((f) => f._status && f._status.estado !== ESTADO_FIANZA.ACTIVA).length;
        const exportRows = filteredProveedores.map((p) => [
          p.label,
          p.rfc || '',
          p.convenioNombre,
          p.counts.pendiente,
          p.counts.overdue,
          p.counts.vigente,
          p.counts.vencida,
          terminadasOf(p),
          p.montos.pendiente,
          p.montos.overdue,
          p.montos.vencida,
          p.montos.total,
        ]);
        // The aggregate's emitidas/pendientes reshaped into the panel's rollup —
        // same shape ProveedorCard builds, client side only (no prima/pago).
        const panelStatsFor = (p) => ({
          rows: (p.emitidas || []).map((f) => ({
            fianza: f,
            status: f._status,
            prima: 0,
            pagoEstado: 'pendiente',
            pagadas: 0,
            facturasTotal: 0,
            endososPendientes: 0,
          })),
          pendientes: p.pendientes || [],
        });
        return (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
              <Text className="text-sm text-gray-600">
                Listado completo de proveedores en tus convenios — ordenado por exposición vencida y pendiente.
              </Text>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={proveedorSearch}
                  onChange={(e) => setProveedorSearch(e.target.value)}
                  placeholder="Buscar proveedor, RFC o convenio..."
                  className="w-full sm:max-w-xs px-3 py-1.5 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
                />
                <button
                  onClick={() => downloadCSV(
                    'convenios-proveedores.csv',
                    ['Proveedor', 'RFC', 'Convenio', 'Pendientes', 'Atrasadas', 'Vigentes', 'Vencidas', 'Terminadas', 'Monto pendiente', 'Monto atrasado', 'Monto vencido', 'Monto total'],
                    exportRows
                  )}
                  disabled={exportRows.length === 0}
                  className="text-sm px-3 py-1.5 rounded-md border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 disabled:opacity-50"
                >
                  Descargar CSV
                </button>
              </div>
            </div>
            {filteredProveedores.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <Text>
                  {proveedorSearch.trim()
                    ? 'Ningún proveedor coincide con la búsqueda.'
                    : 'No hay proveedores en tus convenios.'}
                </Text>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                      <TableRow>
                        <TableHeader className="text-white font-semibold py-3 w-8" />
                        <TableHeader className="text-white font-semibold py-3">Proveedor</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">RFC</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Convenio</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Pendientes</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Atrasadas</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Vigentes</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Vencidas</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Terminadas</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto pendiente</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto atrasado</TableHeader>
                        <TableHeader className="text-white font-semibold py-3">Monto vencido</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProveedores.map((p, idx) => {
                        const rowKey = `${p.convenioId}_${p.key}`;
                        const expanded = expandedProveedorKeys.has(rowKey);
                        const terminadas = terminadasOf(p);
                        return (
                        <React.Fragment key={rowKey}>
                        <TableRow
                          className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                          onClick={() => navigate(`/app/portal/convenios/${p.convenioId}?tab=proveedores&proveedor=${encodeURIComponent(p.key)}`)}
                        >
                          <TableCell className="py-3 pr-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleProveedorExpanded(rowKey);
                              }}
                              className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                              title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                            >
                              {expanded ? (
                                <ChevronDownIcon className="w-4 h-4" />
                              ) : (
                                <ChevronRightIcon className="w-4 h-4" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="py-3"><Text className="text-sm font-semibold text-azul-surelia">{p.label}</Text></TableCell>
                          <TableCell className="py-3 font-mono text-xs">{p.rfc || '-'}</TableCell>
                          <TableCell className="py-3 text-sm">{p.convenioNombre}</TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${p.counts.pendiente > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                              {p.counts.pendiente}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${p.counts.overdue > 0 ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-gray-400'}`}>
                              {p.counts.overdue}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${p.counts.vigente > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                              {p.counts.vigente}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${p.counts.vencida > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`}>
                              {p.counts.vencida}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${terminadas > 0 ? 'bg-zinc-200 text-zinc-700' : 'bg-gray-100 text-gray-400'}`}>
                              {terminadas}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 text-sm font-semibold text-azul-surelia">{formatMXN(p.montos.pendiente)}</TableCell>
                          <TableCell className="py-3 text-sm text-amber-800 font-semibold">{p.montos.overdue > 0 ? formatMXN(p.montos.overdue) : '-'}</TableCell>
                          <TableCell className="py-3 text-sm text-red-700 font-semibold">{p.montos.vencida > 0 ? formatMXN(p.montos.vencida) : '-'}</TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow className="bg-azul-surelia/5">
                            <TableCell colSpan={12} className="py-3 px-6">
                              <ContratoFianzasPanel
                                stats={panelStatsFor(p)}
                                contratoId={null}
                                onFianzaClick={(_contratoId, fianza) =>
                                  openFianzaView(fianza, fianza.contractId, p.convenioId)
                                }
                                canUpload={false}
                                mode="client"
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* The viewers below are fixed-position overlays: keep them OUT of this
          space-y wrapper, whose sibling margin-top would shove a fixed top-0
          box down and leave a gap above it. */}
      <div className="space-y-6">
        <div>
          <Heading level={1} className="text-azul-surelia">Reportes</Heading>
          <Text className="text-azul-surelia/70 mt-1">
            Selecciona un reporte para ver el detalle.
          </Text>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {REPORTS.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              selected={report.id === selectedReportId}
              onSelect={setSelectedReportId}
            />
          ))}
        </div>

        {renderReport()}
      </div>

      {viewContrato && (
        <ContratoView
          contrato={viewContrato.contract}
          isOpen
          onClose={() => setViewContrato(null)}
          convenio={viewContrato.convenio}
          beneficiariosById={beneficiariosByIdFor(viewContrato.convenio)}
          clientData={viewContrato.contract.clientId ? clientsById[viewContrato.contract.clientId] : undefined}
          onUpdate={() => {}}
        />
      )}

      {viewFianza && (
        <FianzaView
          fianza={viewFianza.fianza}
          contratoData={viewFianza.contract}
          convenio={viewFianza.convenio}
          beneficiariosById={beneficiariosByIdFor(viewFianza.convenio)}
          clientData={viewFianza.contract.clientId ? clientsById[viewFianza.contract.clientId] : undefined}
          isOpen
          onClose={() => setViewFianza(null)}
        />
      )}
    </div>
  );
};

export default ClientPortalReportes;
